import { createContext, useContext, useReducer, type ReactNode } from "react";
import type { DraftListEntry } from "../components/draft-list/draft-item.component";

// Replaces draftsReducer's localDraftsList slice — client-only unsaved-draft
// scratch space, read/written across two routes (/invoice/create and
// /company-setting), so it's mounted once at the app root rather than scoped
// to either page. A 1:1 port of the 3 existing reducer cases, not a redesign.
//
// Input payloads are a loose bag (matching AddDraftBillVars/SaveDraftVars's
// precedent in bill.queries.ts/drafts.queries.ts) rather than DraftListEntry
// itself — callers pass formik values mid-edit (numeric fields can be
// `string | number` while the user is typing), not the strict server-shaped
// Draft fields DraftListEntry intersects.
type LocalDraftInput = Record<string, unknown>;

type LocalDraftsAction =
	| { type: "add"; payload: LocalDraftInput }
	| { type: "set"; payload: LocalDraftInput[] }
	| { type: "update"; payload: LocalDraftInput };

function localDraftsReducer(state: DraftListEntry[], action: LocalDraftsAction): DraftListEntry[] {
	switch (action.type) {
		case "add":
			return [...state, action.payload as DraftListEntry];
		case "set":
			return action.payload as DraftListEntry[];
		case "update": {
			const index = state.findIndex((draft) => draft.invoice_number === action.payload.invoice_number);
			if (index === -1) return state;

			const updated: DraftListEntry = {
				...state[index],
				...action.payload,
				buyer_pan: (action.payload.buyer_pan as string | undefined)?.toString() || null,
				invoice_number: (action.payload.invoice_number as string | undefined)?.toString(),
				discount: parseFloat(String(action.payload.discount ?? 0)),
				service_charge: parseFloat(String(action.payload.service_charge ?? 0)),
			};

			return [...state.slice(0, index), updated, ...state.slice(index + 1)];
		}
		default:
			return state;
	}
}

interface LocalDraftsContextValue {
	localDraftsList: DraftListEntry[];
	addLocalDraft: (draft: LocalDraftInput) => void;
	setLocalDraftsList: (drafts: LocalDraftInput[]) => void;
	updateLocalDraft: (draft: LocalDraftInput) => void;
}

const LocalDraftsContext = createContext<LocalDraftsContextValue | null>(null);

export function LocalDraftsProvider({ children }: { children: ReactNode }) {
	const [localDraftsList, dispatch] = useReducer(localDraftsReducer, []);

	const value: LocalDraftsContextValue = {
		localDraftsList,
		addLocalDraft: (draft) => dispatch({ type: "add", payload: draft }),
		setLocalDraftsList: (drafts) => dispatch({ type: "set", payload: drafts }),
		updateLocalDraft: (draft) => dispatch({ type: "update", payload: draft }),
	};

	return <LocalDraftsContext.Provider value={value}>{children}</LocalDraftsContext.Provider>;
}

export function useLocalDrafts(): LocalDraftsContextValue {
	const context = useContext(LocalDraftsContext);
	if (!context) {
		throw new Error("useLocalDrafts must be used within a LocalDraftsProvider");
	}
	return context;
}
